import styles from 'css/src/exports.module.scss';

import type { ChangeEventHandler, InputHTMLAttributes, ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';

import CheckboxCheckedIcon from 'js/src/libs/custom_icons/CheckboxCheckedIcon';
import CheckboxIndeterminateIcon from 'js/src/libs/custom_icons/CheckboxIndeterminateIcon';
import CheckboxUncheckedIcon from 'js/src/libs/custom_icons/CheckboxUncheckedIcon';
import uuid from 'js/src/libs/uuid';

export type CheckboxValue = InputHTMLAttributes<HTMLInputElement>['value'] | boolean;

export type CheckboxProps = {
    name?: string;
    value?: CheckboxValue;
    text?: ReactNode;
    description?: ReactNode;
    checked?: boolean;
    indeterminate?: boolean;
    readOnly?: boolean;
    disabled?: boolean;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    id?: string;
    className?: string;
    'data-testid'?: string;
};

const CheckboxPresentation = ({
    name,
    value,
    onChange,
    text = '',
    description = '',
    checked = false,
    indeterminate = false,
    readOnly = false,
    disabled = false,
    id = `checkbox-${uuid()}`,
    className = '',
    'data-testid': dataTestId,
}: CheckboxProps) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const fill = disabled
        ? styles['allColors-color-grey-400']
        : styles['allColors-color-l5-primary-500'];

    const isChecked = checked && !indeterminate;
    const isUnchecked = !checked && !indeterminate;
    const isIndeterminate = indeterminate;
    const isDisabled = disabled;
    const isReadOnly = readOnly;

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.indeterminate = indeterminate;
        }
    }, [indeterminate]);

    return (
        <label
            id={id}
            className={classNames(
                'checkbox d-flex align-items-center cursor-pointer',
                {
                    'is-checked': isChecked,
                    'is-unchecked': isUnchecked,
                    'is-indeterminate': isIndeterminate,
                    'is-disabled': isDisabled,
                    'is-readonly': isReadOnly,
                    'pe-none': isDisabled || isReadOnly,
                },
                className,
            )}
        >
            <input
                name={name}
                value={typeof value === 'boolean' ? value.toString() : value}
                type="checkbox"
                checked={checked}
                readOnly={isReadOnly}
                disabled={isDisabled}
                onChange={onChange}
                className="d-none"
                ref={inputRef}
                data-testid={dataTestId}
            />
            <div>
                {isChecked && <CheckboxCheckedIcon fill={fill} />}
                {isUnchecked && <CheckboxUncheckedIcon fill={fill} />}
                {isIndeterminate && <CheckboxIndeterminateIcon fill={fill} />}
            </div>
            {text && (
                <div className="ms-l5-8">
                    <div
                        className={classNames('font-l5 font-l5--small', {
                            'color-grey-900': !isDisabled,
                            'color-grey-400': isDisabled,
                        })}
                    >
                        {text}
                    </div>
                    {description && (
                        <div
                            className={classNames('font-l5 font-l5--small mt-l5-4', {
                                'color-grey-600': !isDisabled,
                                'color-grey-400': isDisabled,
                            })}
                        >
                            {description}
                        </div>
                    )}
                </div>
            )}
        </label>
    );
};

const Checkbox = React.memo(CheckboxPresentation);
export default Checkbox;
